import { Fragment, useState } from 'react'
import { ArrowDown, ArrowUp, ChevronDown, Plus, Trash2, X } from 'lucide-react'
import Field, { inputClass } from '../Field'
import Select from '../Select'
import { useAuth } from '../../hooks/useAuth'
import { surveyEmailHtml } from '../../lib/surveyEmail'
import { partnerLabel, partnerLines, servedBy } from '../../lib/surveyPartners'
import type { SurveyArea, SurveyDepartment, SurveyDesign, SurveyPartner, SurveyQuestion, SurveyQuestionKind } from '../../types'

const newId = (prefix: string) => `${prefix}_${Math.random().toString(36).slice(2, 10)}`

/** A Settings list plus the value already chosen, so a value Settings no
 *  longer has still shows rather than reading as unset. */
const withValue = (list: string[], value: string) => (value && !list.includes(value) ? [...list, value] : list)

const KINDS: readonly (readonly [SurveyQuestionKind, string])[] = [
  ['rating', 'Star rating'],
  ['scale', '0 to 10'],
  ['choice', 'Choice'],
  ['text', 'Written answer'],
]

export const STANDARD_STARS = ['Significantly below expectations', 'Below expectations', 'Good', 'Excellent']

const iconButton = 'p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent'
const addButton = 'flex items-center gap-1.5 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium transition-colors'
const th = 'text-left font-semibold px-3 py-2'

export function newQuestion(questions: SurveyQuestion[]): SurveyQuestion {
  return {
    id: '', kind: 'rating', title: '', text: '', required: true,
    labels: questions.find(q => q.kind === 'rating')?.labels ?? STANDARD_STARS,
    lowLabel: '', highLabel: '', options: [], naLabel: '',
  }
}

// ── Questions ────────────────────────────────────────────────────────────────

export function QuestionsEditor({ questions, onChange }: { questions: SurveyQuestion[]; onChange: (q: SurveyQuestion[]) => void }) {
  // One question open at a time; the rest stay one line each.
  const [open, setOpen] = useState<number | null>(null)
  const set = (i: number, patch: Partial<SurveyQuestion>) => onChange(questions.map((q, j) => (j === i ? { ...q, ...patch } : q)))
  const move = (i: number, by: number) => {
    const next = [...questions]
    const [q] = next.splice(i, 1)
    next.splice(i + by, 0, q)
    onChange(next)
    setOpen(i + by)
  }
  const kindLabel = (kind: SurveyQuestionKind) => KINDS.find(([k]) => k === kind)?.[1] ?? kind
  return (
    <div className="space-y-2">
      {questions.length === 0 && <p className="card px-4 py-6 text-center text-sm text-slate-400">No questions yet.</p>}
      {questions.map((q, i) => (
        <div key={q.id || `new-${i}`} className={`card overflow-hidden ${open === i ? 'ring-2 ring-[#00ACED]/20 border-[#00ACED]' : ''}`}>
          <button type="button" onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-slate-50/60">
            <span className="w-7 h-7 rounded-lg bg-[#005EB8]/10 text-[#005EB8] text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
            <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded bg-slate-100 text-slate-500 w-32 whitespace-nowrap text-center flex-shrink-0">{kindLabel(q.kind)}</span>
            <span className="font-semibold text-sm text-slate-800 w-48 truncate flex-shrink-0">{q.title || <span className="text-slate-300">No title</span>}</span>
            <span className="text-sm text-slate-500 truncate flex-1">{q.text || <span className="text-slate-300">No question yet</span>}</span>
            {!q.required && <span className="text-[11px] text-slate-400 flex-shrink-0">Optional</span>}
            {q.naLabel && q.kind !== 'text' && <span className="text-[11px] text-slate-400 flex-shrink-0">+ {q.naLabel}</span>}
            <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${open === i ? 'rotate-180' : ''}`} />
          </button>

          {open === i && (
            <div className="px-5 pb-5 pt-4 border-t border-slate-100">
              <div className="flex items-center gap-3 mb-4">
                <Select value={q.kind} onChange={kind => set(i, { kind: kind as SurveyQuestionKind, labels: kind === 'rating' && !q.labels.length ? STANDARD_STARS : q.labels })}
                  options={KINDS} className="w-44" />
                <label className="flex items-center gap-2 text-sm text-slate-600">
                  <input type="checkbox" checked={q.required} onChange={e => set(i, { required: e.target.checked })} className="rounded border-slate-300" />
                  Required
                </label>
                <div className="ml-auto flex items-center">
                  <button type="button" className={iconButton} disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up"><ArrowUp className="w-4 h-4" /></button>
                  <button type="button" className={iconButton} disabled={i === questions.length - 1} onClick={() => move(i, 1)} aria-label="Move down"><ArrowDown className="w-4 h-4" /></button>
                  <button type="button" className={iconButton} onClick={() => { onChange(questions.filter((_, j) => j !== i)); setOpen(null) }} aria-label="Remove question"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>

              <div className="grid grid-cols-[240px_1fr] gap-4">
                <Field label="Title" info="The short name used in Reports.">
                  <input className={inputClass} value={q.title} onChange={e => set(i, { title: e.target.value })} placeholder="Overall experience" />
                </Field>
                <Field label="Question" info={<p><strong>{'{partner}'}</strong> becomes the person's partner team, e.g. IT SF (Finance, DBS, GS).</p>}>
                  <input className={inputClass} value={q.text} onChange={e => set(i, { text: e.target.value })} placeholder="How would you rate your overall experience working with {partner}?" />
                </Field>
              </div>

              {q.kind === 'rating' && (
                <Field label="Answers, lowest first" className="mt-4" action={
                  <span className="flex items-center">
                    <button type="button" className={iconButton} disabled={q.labels.length >= 10} onClick={() => set(i, { labels: [...q.labels, ''] })} aria-label="Add a star"><Plus className="w-4 h-4" /></button>
                    <button type="button" className={iconButton} disabled={q.labels.length <= 2} onClick={() => set(i, { labels: q.labels.slice(0, -1) })} aria-label="Remove the top star"><X className="w-4 h-4" /></button>
                  </span>
                }>
                  <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(q.labels.length, 5)}, minmax(0, 1fr))` }}>
                    {q.labels.map((label, k) => (
                      <div key={k} className="flex items-center gap-1.5">
                        <span className="text-xs font-bold text-[#F88806] tabular-nums w-6 text-right flex-shrink-0">{k + 1}★</span>
                        <input className={inputClass} value={label}
                          onChange={e => set(i, { labels: q.labels.map((l, m) => (m === k ? e.target.value : l)) })} />
                      </div>
                    ))}
                  </div>
                </Field>
              )}

              {q.kind === 'scale' && (
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <Field label="0 means"><input className={inputClass} value={q.lowLabel} onChange={e => set(i, { lowLabel: e.target.value })} placeholder="Not at all likely" /></Field>
                  <Field label="10 means"><input className={inputClass} value={q.highLabel} onChange={e => set(i, { highLabel: e.target.value })} placeholder="Extremely likely" /></Field>
                </div>
              )}

              {q.kind === 'choice' && <Options options={q.options} onChange={options => set(i, { options })} />}

              {q.kind !== 'text' && (
                <Field label="Not applicable answer" className="mt-4 w-72" info="Offered alongside the answers and left out of scores. Blank: not offered.">
                  <input className={inputClass} value={q.naLabel} onChange={e => set(i, { naLabel: e.target.value })} placeholder="Not used" />
                </Field>
              )}
            </div>
          )}
        </div>
      ))}
      <button type="button" className={addButton} onClick={() => { onChange([...questions, newQuestion(questions)]); setOpen(questions.length) }}>
        <Plus className="w-4 h-4" /> Add question
      </button>
    </div>
  )
}

function Options({ options, onChange }: { options: string[]; onChange: (o: string[]) => void }) {
  const [draft, setDraft] = useState('')
  const add = () => {
    const value = draft.trim()
    if (value && !options.includes(value)) onChange([...options, value])
    setDraft('')
  }
  return (
    <Field label="Options" className="mt-4">
      <div className="flex flex-wrap items-center gap-2">
        {options.map(o => (
          <span key={o} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium">
            {o}
            <button type="button" onClick={() => onChange(options.filter(x => x !== o))} className="text-slate-400 hover:text-slate-700"><X className="w-3.5 h-3.5" /></button>
          </span>
        ))}
        <input className={`${inputClass} w-48`} value={draft} onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add() } }} placeholder="Add an option" />
      </div>
    </Field>
  )
}

// ── Partner teams and departments ────────────────────────────────────────────

export function TeamsEditor({ design, onChange, settingsTeams, settingsPortfolios }: {
  design: SurveyDesign
  onChange: (patch: Partial<SurveyDesign>) => void
  settingsTeams: string[]
  settingsPortfolios: string[]
}) {
  const partners = design.partners
  const departments = design.departments
  const setPartner = (i: number, patch: Partial<SurveyPartner>) =>
    onChange({ partners: partners.map((p, j) => (j === i ? { ...p, ...patch } : p)) })
  const setDepartment = (i: number, patch: Partial<SurveyDepartment>) =>
    onChange({ departments: departments.map((d, j) => (j === i ? { ...d, ...patch } : d)) })
  const removePartner = (id: string) => onChange({
    partners: partners.filter(p => p.id !== id),
    departments: departments.map(d => ({
      ...d,
      partnerId: d.partnerId === id ? '' : d.partnerId,
      ...(d.areas ? { areas: d.areas.map(a => (a.partnerId === id ? { ...a, partnerId: '' } : a)) } : {}),
    })),
  })
  const unusedPortfolios = settingsPortfolios.filter(p => !departments.some(d => d.portfolio === p))
  const addPortfolios = () => onChange({
    departments: [...departments, ...unusedPortfolios.map(p => ({ id: newId('d'), name: p, partnerId: '', portfolio: p }))],
  })
  const served = (id: string) => servedBy(design, id).length
  // A department is split by choosing "Split into areas" as its partner team.
  const SPLIT = '__areas'
  const newArea = (partnerId = ''): SurveyArea => ({ id: newId('a'), name: '', definition: '', partnerId })
  const setTeam = (i: number, value: string) => setDepartment(i, value === SPLIT
    ? { partnerId: '', areas: [newArea(departments[i].partnerId), newArea()] }
    : { partnerId: value, areas: [] })
  const setArea = (i: number, j: number, patch: Partial<SurveyArea>) =>
    setDepartment(i, { areas: (departments[i].areas ?? []).map((a, k) => (k === j ? { ...a, ...patch } : a)) })

  return (
    <div className="space-y-6">
      <div className="card p-6">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-bold text-slate-800">Partner teams</h2>
        </div>
        <p className="text-sm text-slate-500 mb-4">The IT teams people rate. Leads are for reference and never shown.</p>
        <div className="overflow-hidden rounded-lg border border-slate-100 mb-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                <th className={th}>Settings team</th><th className={th}>Name on the survey</th><th className={th}>Lead</th>
                <th className={`${th} text-right`}>Departments</th><th className="w-10" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {partners.map((p, i) => (
                <tr key={p.id}>
                  <td className="px-3 py-2 w-48">
                    <Select value={p.team} onChange={team => setPartner(i, { team, name: p.name || (team ? `IT ${team}` : '') })}
                      options={[['', 'Not in Settings'] as const, ...withValue(settingsTeams, p.team).map(t => [t, t] as const)]} />
                  </td>
                  <td className="px-3 py-2"><input className={inputClass} value={p.name} onChange={e => setPartner(i, { name: e.target.value })} /></td>
                  <td className="px-3 py-2 w-56"><input className={inputClass} value={p.lead} onChange={e => setPartner(i, { lead: e.target.value })} placeholder="Johanna Lucas" /></td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-500">{served(p.id)}</td>
                  <td className="px-2 py-2"><button type="button" className={iconButton} onClick={() => removePartner(p.id)} aria-label="Remove"><Trash2 className="w-4 h-4" /></button></td>
                </tr>
              ))}
              {partners.length === 0 && <tr><td colSpan={5} className="px-3 py-4 text-center text-xs text-slate-400">No partner teams: results are shown by department.</td></tr>}
            </tbody>
          </table>
        </div>
        <button type="button" className={addButton}
          onClick={() => onChange({ partners: [...partners, { id: newId('p'), name: '', lead: '', team: '' }] })}>
          <Plus className="w-4 h-4" /> Add partner team
        </button>
      </div>

      <div className="card p-6">
        <h2 className="font-bold text-slate-800 mb-1">Departments</h2>
        <p className="text-sm text-slate-500 mb-4">Where people answer from. Split one into areas if its parts work with different teams.</p>
        <div className="overflow-hidden rounded-lg border border-slate-100 mb-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                <th className={th}>Department</th><th className={th}>Partner team</th><th className={th}>Settings portfolio</th><th className="w-10" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {departments.map((d, i) => (
                <Fragment key={d.id}>
                <tr>
                  <td className="px-3 py-2"><input className={inputClass} value={d.name} onChange={e => setDepartment(i, { name: e.target.value })} /></td>
                  <td className="px-3 py-2 w-64">
                    <Select value={d.areas?.length ? SPLIT : d.partnerId} onChange={value => setTeam(i, value)}
                      options={[
                        ['', 'No partner team'] as const,
                        ...partners.map(p => [p.id, p.name || 'Unnamed team'] as const),
                        ...(partners.length > 1 ? [[SPLIT, 'Split into areas'] as const] : []),
                      ]} />
                  </td>
                  <td className="px-3 py-2 w-56">
                    <Select value={d.portfolio} onChange={portfolio => setDepartment(i, { portfolio })}
                      options={[['', 'None'] as const, ...withValue(settingsPortfolios, d.portfolio).map(p => [p, p] as const)]} />
                  </td>
                  <td className="px-2 py-2"><button type="button" className={iconButton} onClick={() => onChange({ departments: departments.filter(x => x.id !== d.id) })} aria-label="Remove"><Trash2 className="w-4 h-4" /></button></td>
                </tr>
                {!!d.areas?.length && (
                  <tr>
                    <td colSpan={4} className="px-3 pb-3 pt-0">
                      <div className="ml-6 pl-4 border-l-2 border-[#00AEEF]/40 space-y-2">
                        <div className="text-[11px] text-slate-500">{d.name || 'This department'} picks one area, so keep each definition clear.</div>
                        {d.areas.map((a, j) => (
                          <div key={a.id} className="grid grid-cols-[12rem_1fr_14rem_auto] gap-2 items-start">
                            <input className={inputClass} value={a.name} placeholder="Area, e.g. Front Office" aria-label="Area name"
                              onChange={e => setArea(i, j, { name: e.target.value })} />
                            <textarea className={`${inputClass} resize-y leading-snug`} rows={3} value={a.definition} aria-label="What the area covers"
                              placeholder="What this area covers, so people choose the right one" onChange={e => setArea(i, j, { definition: e.target.value })} />
                            <Select value={a.partnerId} onChange={partnerId => setArea(i, j, { partnerId })}
                              options={[['', 'Choose a partner team'] as const, ...partners.map(p => [p.id, p.name || 'Unnamed team'] as const)]} />
                            <button type="button" className={iconButton} disabled={d.areas!.length <= 2} aria-label="Remove area"
                              onClick={() => setDepartment(i, { areas: d.areas!.filter(x => x.id !== a.id) })}><Trash2 className="w-4 h-4" /></button>
                          </div>
                        ))}
                        <button type="button" className="text-xs font-semibold text-[#005EB8] hover:underline flex items-center gap-1"
                          onClick={() => setDepartment(i, { areas: [...d.areas!, newArea()] })}>
                          <Plus className="w-3.5 h-3.5" /> Add area
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" className={addButton}
            onClick={() => onChange({ departments: [...departments, { id: newId('d'), name: '', partnerId: '', portfolio: '' }] })}>
            <Plus className="w-4 h-4" /> Add department
          </button>
          {unusedPortfolios.length > 0 && (
            <button type="button" className={addButton} onClick={addPortfolios}>
              <Plus className="w-4 h-4" /> Add Settings portfolios ({unusedPortfolios.length})
            </button>
          )}
        </div>
        <Field label="No partner team? Questions ask about" className="mt-5 w-80">
          <input className={inputClass} value={design.partnerFallback} onChange={e => onChange({ partnerFallback: e.target.value })} placeholder="IT" />
        </Field>
      </div>
    </div>
  )
}

// ── Email ────────────────────────────────────────────────────────────────────

export function EmailEditor({ design, onChange }: { design: SurveyDesign; onChange: (patch: Partial<SurveyDesign>) => void }) {
  const [as, setAs] = useState(design.departments[0]?.id ?? '')
  const [reminder, setReminder] = useState(false)
  const [height, setHeight] = useState(900)
  // Addressed to whoever is looking, as they would get it.
  const to = useAuth().user?.name || 'Sam Taylor'
  const partner = partnerLabel(design, as)
  const subject = design.emailSubject.replace(/\{partner\}/g, partner).trim() || 'Your feedback is needed'
  const html = surveyEmailHtml({
    body: design.emailBody, partner, partners: partnerLines(design), url: '#', reminder, name: to,
    banner: '/brand/survey-email-banner.png',
  })
  return (
    <div className="grid grid-cols-2 gap-6 items-start">
      <div className="card p-6 space-y-4">
        <Field label="Subject">
          <input className={inputClass} value={design.emailSubject} onChange={e => onChange({ emailSubject: e.target.value })} />
        </Field>
        <Field label="Message" info={<><p><strong>{'{partner}'}</strong>: the reader's partner team.</p><p><strong>{'{partners}'}</strong>: every partner team with its lead and departments.</p><p>Each email opens with the person's first name, e.g. Hi Sam.</p><p>A blank line starts a new paragraph.</p></>}>
          <textarea className={`${inputClass} resize-y leading-relaxed`} rows={12} value={design.emailBody} onChange={e => onChange({ emailBody: e.target.value })} />
        </Field>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          Insert
          {['{partner}', '{partners}'].map(token => (
            <button key={token} type="button" onClick={() => onChange({ emailBody: `${design.emailBody}${design.emailBody.endsWith('\n') || !design.emailBody ? '' : ' '}${token}` })}
              className="px-2 py-0.5 rounded-md bg-[#00ACED]/10 text-[#0077A8] font-mono font-semibold hover:bg-[#00ACED]/20">{token}</button>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="flex items-center rounded-lg border border-slate-200 overflow-hidden text-xs font-semibold bg-white">
            {([[false, 'Invitation'], [true, 'Reminder']] as const).map(([value, label]) => (
              <button key={label} type="button" onClick={() => setReminder(value)}
                className={`px-3 py-1.5 transition-colors ${reminder === value ? 'bg-[#005EB8] text-white' : 'text-slate-500 hover:bg-slate-50'}`}>
                {label}
              </button>
            ))}
          </div>
          {design.departments.length > 0 && (
            <Select value={as} onChange={setAs} options={design.departments.map(d => [d.id, `As ${d.name}`] as const)} className="w-56" />
          )}
        </div>
        {/* The email itself, as the backend builds it, under an Outlook-style
            header. No scripts run in the frame; same-origin only so its
            height can be read. */}
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
          <div className="px-5 py-3 border-b border-slate-200">
            <div className="text-[15px] font-semibold text-slate-900 truncate">{reminder ? `Reminder: ${subject}` : subject}</div>
            <div className="text-[11px] text-slate-500 mt-0.5">From: IT &amp; Data NE - TrackIT · To: {to}</div>
          </div>
          <iframe title="Email preview" srcDoc={html} sandbox="allow-same-origin" className="block w-full border-0" style={{ height }}
            onLoad={e => setHeight(e.currentTarget.contentDocument?.body.scrollHeight ?? 900)} />
        </div>
      </div>
    </div>
  )
}
